import { useLayoutEffect, useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Object3D } from 'three';
import { seededRandom } from './random.js';
import { useCelestial } from './CelestialProvider.jsx';

const dummy = new Object3D();

// Instanced rock band lying IN a ring plane (tilt matches RingedPlanet's ring)
// — tight radial spread + tiny vertical jitter keeps it "not messy".
export function AsteroidRing({ inner = 1.25, outer = 1.95, tilt = 0.42, scale = 1, spin = 0.05 }) {
  const meshRef = useRef();
  const groupRef = useRef();
  const { perfTier, seeds } = useCelestial();
  const count = Math.floor(perfTier.asteroids * 0.6);

  const matrices = useMemo(() => {
    const random = seededRandom(seeds.asteroids);
    const next = [];
    for (let i = 0; i < count; i++) {
      const angle = random() * Math.PI * 2;
      const radius = inner + random() * (outer - inner);
      dummy.position.set(Math.cos(angle) * radius, (random() - 0.5) * 0.06, Math.sin(angle) * radius);
      dummy.scale.setScalar(0.014 + random() * 0.03);
      dummy.rotation.set(angle * 1.3, angle * 0.65, angle * 0.3);
      dummy.updateMatrix();
      next.push(dummy.matrix.clone());
    }
    return next;
  }, [count, seeds.asteroids, inner, outer]);

  useLayoutEffect(() => {
    if (!meshRef.current) return;
    matrices.forEach((matrix, index) => meshRef.current.setMatrixAt(index, matrix));
    meshRef.current.instanceMatrix.needsUpdate = true;
  }, [matrices]);

  useFrame((state) => {
    if (!groupRef.current) return;
    groupRef.current.rotation.y = state.clock.elapsedTime * spin;
  });

  return (
    <group rotation={[tilt, 0, 0]} scale={scale}>
      <group ref={groupRef}>
        <instancedMesh ref={meshRef} args={[undefined, undefined, count]}>
          <dodecahedronGeometry args={[1, 0]} />
          <meshStandardMaterial color="#8d93a3" roughness={0.92} />
        </instancedMesh>
      </group>
    </group>
  );
}
